import { useEffect, useState } from "react";

import SaveEntityToLocation from "@/components/SaveEntityToLocation";
import Checkbox from "@/components/ui/checkbox";
import { EntityIcon } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EntityType } from "@/queries";
import type { PlaygroundEvaluateExportPrompt } from "@/queries/playground/evaluate";
import type { PromptVersion } from "@/types/apiGetters";

interface CreateTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftSheetCount: number;
  defaultTableName: string;
  defaultPromptName: string;
  currentFolderId: number | null;
  selectedTemplateVersion?: PromptVersion;
  selectedPromptName?: string;
  canCreatePrompt: boolean;
  canEditPrompt: boolean;
  onCreate: (
    tableName: string,
    folderId: number | null,
    prompt: PlaygroundEvaluateExportPrompt,
  ) => Promise<void>;
}

const CreateTableDialog = ({
  open,
  onOpenChange,
  draftSheetCount,
  defaultTableName,
  defaultPromptName,
  currentFolderId,
  selectedTemplateVersion,
  selectedPromptName,
  canCreatePrompt,
  canEditPrompt,
  onCreate,
}: CreateTableDialogProps) => {
  const canKeepLinked = Boolean(selectedTemplateVersion && canEditPrompt);
  const canCreateLinkedPrompt = !selectedTemplateVersion && canCreatePrompt;
  const [shouldLinkPrompt, setShouldLinkPrompt] = useState(canKeepLinked);
  const [promptName, setPromptName] = useState(defaultPromptName);

  useEffect(() => {
    if (!open) {
      return;
    }

    setShouldLinkPrompt(canKeepLinked);
    setPromptName(defaultPromptName);
  }, [canKeepLinked, defaultPromptName, open, selectedTemplateVersion?.id]);

  const createTableScope =
    draftSheetCount === 1
      ? "this draft as a Table"
      : `these ${draftSheetCount} draft sheets as one Table`;
  const promptNameRequired = canCreateLinkedPrompt && shouldLinkPrompt;
  const isPromptNameInvalid = promptNameRequired && !promptName.trim();
  const saveButtonText = promptNameRequired ? "Create Table & Prompt" : "Create Table";

  const handleCreate = async (tableName: string, folderId: number | null) => {
    let prompt: PlaygroundEvaluateExportPrompt = { action: "table_only" };

    if (selectedTemplateVersion && shouldLinkPrompt) {
      prompt = {
        action: "ensure_linked",
        prompt_registry_id: selectedTemplateVersion.prompt_id,
        prompt_version_number: selectedTemplateVersion.number,
        parent_version_id: selectedTemplateVersion.id,
      };
    } else if (!selectedTemplateVersion && shouldLinkPrompt) {
      prompt = { action: "create_prompt", prompt_name: promptName.trim() };
    }

    await onCreate(tableName, folderId, prompt);
  };

  return (
    <SaveEntityToLocation
      type={EntityType.SMART_TABLE}
      onSave={handleCreate}
      currentFolderId={currentFolderId}
      defaultName={defaultTableName}
      open={open}
      onOpenChange={onOpenChange}
      title="Create Table"
      description={`Save ${createTableScope} in your workspace.`}
      saveButtonText={saveButtonText}
      isSaveDisabled={isPromptNameInvalid}
      footerContent={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {canKeepLinked && selectedTemplateVersion && (
            <label
              htmlFor="create-table-keep-prompt-linked"
              className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5"
            >
              <Checkbox
                id="create-table-keep-prompt-linked"
                checked={shouldLinkPrompt}
                onChange={event => setShouldLinkPrompt(event.target.checked)}
                className="mt-0.5"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <EntityIcon type={EntityType.PROMPT} size="xs" className="shrink-0" />
                  <span className="truncate">
                    Link Table to {selectedPromptName || "this Prompt Template"}
                  </span>
                </span>
                <span className="block text-xs leading-5 text-gray-500">
                  {shouldLinkPrompt
                    ? `Use v${selectedTemplateVersion.number} if unchanged, or save Playground changes as a new version.`
                    : "The current prompt will be kept in this Table only."}
                </span>
              </span>
            </label>
          )}

          {canCreateLinkedPrompt && (
            <>
              <label
                htmlFor="create-table-create-prompt"
                className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5"
              >
                <Checkbox
                  id="create-table-create-prompt"
                  checked={shouldLinkPrompt}
                  onChange={event => setShouldLinkPrompt(event.target.checked)}
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <EntityIcon type={EntityType.PROMPT} size="xs" className="shrink-0" />
                    <span>Create a reusable Prompt Template</span>
                  </span>
                  <span className="block text-xs leading-5 text-gray-500">
                    {shouldLinkPrompt
                      ? "Create it in the same folder and link it to this Table."
                      : "Optional — otherwise the prompt stays in this Table only."}
                  </span>
                </span>
              </label>

              {shouldLinkPrompt && (
                <div className="w-full space-y-1.5 pl-7 sm:w-64 sm:pl-0">
                  <Label htmlFor="create-table-prompt-name" className="text-xs text-gray-600">
                    Prompt Name
                  </Label>
                  <Input
                    id="create-table-prompt-name"
                    value={promptName}
                    onChange={event => setPromptName(event.target.value)}
                    placeholder="Enter a prompt name"
                  />
                </div>
              )}
            </>
          )}

          {!canKeepLinked && !canCreateLinkedPrompt && (
            <p className="text-xs leading-5 text-gray-500">
              The current prompt will be kept in this Table only.
            </p>
          )}
        </div>
      }
    />
  );
};

export default CreateTableDialog;
